<template>
  <div
    :class="[
        'absolute top-0 bottom-0 rounded-xl border cursor-default',
        selected ? 'border-white' : 'border-transparent'
      ]"
    :data-clip-region-id="clip.id"
    @mouseenter="isClipHovered = true"
    @mouseleave="clearHoverState"
    @click.stop="selectClip(clip, $event)"
    @contextmenu.prevent.stop="openClipMenu($event, clip)"
    :style="{
        left: clipStart && `${(clipStart.time / videoDuration) * 100}%`,
        right: clipEnd && `${100 - ((clipEnd.time / videoDuration) * 100)}%`
      }"
    data-onboarding-video-snipping="progress-bar-clip"
  >
    <div
      :class="[
        'absolute-cover pointer-events-none rounded-xl',
        {
          'opacity-80': isClipRangeLoaded,
          'timeline-clip-range-shimmer': !isClipRangeLoaded
        }
      ]"
      :style="isClipRangeLoaded
        ? {
            background: clip.color
          }
        : undefined"
    />
    <div
      v-if="clipStart"
      :class="[
        'absolute -left-0.5 top-0 bottom-0 z-20 w-7 flex-center rounded-l-xl cursor-ew-resize transition-colors transition-colors duration-150 ease-out hover:text-charcoal-600 outline-none focus:outline-none focus-visible:outline-none',
        isSelectedMarker(clipStart) ? 'bg-white text-charcoal-600' : 'bg-transparent text-white/90',
        isHandleVisible(clipStart)
          ? 'pointer-events-auto opacity-100'
          : 'pointer-events-none opacity-0'
      ]"
      role="button"
      :tabindex="isHandleVisible(clipStart) ? 0 : -1"
      :aria-hidden="!isHandleVisible(clipStart)"
      aria-label="Drag clip start handle"
      :data-drag-clip-id="clip.id"
      :data-drag-marker-id="clipStart.id"
      @pointerdown="selectMarker(clipStart)"
      @click.stop="selectMarker(clipStart)"
    >
      <span class="h-0 w-0 border-y-[6px] border-y-transparent border-r-[8px] border-r-current" />
    </div>
    <div
      v-if="clipEnd"
      :class="[
        'absolute -right-0.5 top-0 bottom-0 z-20 w-7 flex-center rounded-r-xl cursor-ew-resize transition-colors transition-colors duration-150 ease-out hover:text-charcoal-600 outline-none focus:outline-none focus-visible:outline-none',
        isSelectedMarker(clipEnd) ? 'bg-white text-charcoal-600' : 'bg-transparent text-white/90',
        isHandleVisible(clipEnd)
          ? 'pointer-events-auto opacity-100'
          : 'pointer-events-none opacity-0'
      ]"
      role="button"
      :tabindex="isHandleVisible(clipEnd) ? 0 : -1"
      :aria-hidden="!isHandleVisible(clipEnd)"
      aria-label="Drag clip end handle"
      :data-drag-clip-id="clip.id"
      :data-drag-marker-id="clipEnd.id"
      @pointerdown="selectMarker(clipEnd)"
      @click.stop="selectMarker(clipEnd)"
    >
      <span class="h-0 w-0 border-y-[6px] border-y-transparent border-l-[8px] border-l-current" />
    </div>
    <div
      v-for="marker in sortedMarkers"
      :class="[
          'absolute w-12 h-12 -mt-0.5 -translate-y-full -translate-x-1/2 bg-center bg-cover border border-2 border-black pointer-events-auto',
          {
            'left-0': marker !== clipEnd,
            'left-full': marker === clipEnd,
            'timeline-marker-shimmer': !markerHasSnapshot(marker)
          }
        ]"
      :style="{
          backgroundImage: markerHasSnapshot(marker) ? `url(${marker.snapshot})` : undefined,
          marginLeft: `${markerNudgePx(marker)}px`
        }"
      :data-clip-id="clip.id"
      :data-marker-id="marker.id"
      data-onboarding-video-snipping="clip-marker"
      @pointerdown="selectMarker(marker)"
      @click.stop="selectMarker(marker)"
      @mouseenter="hoveredMarkerId = marker.id"
      @mouseleave="clearHoveredMarker"
    >
    </div>
  </div>
</template>

<script setup>

  const emit = defineEmits(['select-clip', 'select-marker', 'open-clip-menu'])
  const props = defineProps({
    clip: Object,
    selected: Boolean,
    videoDuration: Number,
    activeMarkerId: {
      type: [String, Number],
      default: null
    },
    markerNudges: {
      type: Object,
      default: () => ({})
    }
  });

  const sortedMarkers = computed(() => (props.clip?.markers || []).slice().sort((a, b) => a.time - b.time));
  const clipStart = computed(() => sortedMarkers.value[0] || null);
  const clipEnd = computed(() => sortedMarkers.value[1] || null);
  const isClipHovered = ref(false);
  const hoveredMarkerId = ref(null);

  function selectClip(clip, event = null) {
    emit('select-clip', {
      clip,
      clientX: event?.clientX
    });
  }

  function selectMarker(marker) {
    if (!marker) {
      return;
    }

    emit('select-marker', {
      clip: props.clip,
      marker
    });
  }

  function openClipMenu(event, clip) {
    emit('open-clip-menu', {
      clip,
      clientX: event?.clientX,
      clientY: event?.clientY
    });
  }

  function clearHoveredMarker() {
    hoveredMarkerId.value = null;
  }

  function clearHoverState() {
    isClipHovered.value = false;
    clearHoveredMarker();
  }

  function isHandleVisible(marker) {
    if (!marker) {
      return false;
    }

    if (props.selected) {
      return true;
    }

    if (hoveredMarkerId.value) {
      return hoveredMarkerId.value === marker.id;
    }

    return isClipHovered.value;
  }

  function markerNudgePx(marker) {
    const nudge = Number(props.markerNudges?.[marker?.id]);
    return Number.isFinite(nudge) ? nudge : 0;
  }

  function markerHasSnapshot(marker) {
    return typeof marker?.snapshot === 'string' && marker.snapshot.trim().length > 0;
  }

  function isSelectedMarker(marker) {
    if (!marker || props.activeMarkerId === null || props.activeMarkerId === undefined) {
      return false;
    }

    return String(marker.id) === String(props.activeMarkerId);
  }

  const isClipRangeLoaded = computed(() => {
    const rangeMarkers = sortedMarkers.value.slice(0, 2);
    return rangeMarkers.length >= 2 && rangeMarkers.every(markerHasSnapshot);
  });

</script>

<style scoped>
  @keyframes timeline-clip-range-shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  .timeline-clip-range-shimmer {
    background-color: rgba(255, 255, 255, 0.12);
    background-image: linear-gradient(
      110deg,
      rgba(255, 255, 255, 0.08) 20%,
      rgba(255, 255, 255, 0.3) 38%,
      rgba(255, 255, 255, 0.08) 55%
    );
    background-size: 220% 100%;
    animation: timeline-clip-range-shimmer 1.25s linear infinite;
  }

  @keyframes timeline-marker-shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  .timeline-marker-shimmer {
    background-color: rgba(255, 255, 255, 0.08);
    background-image: linear-gradient(
      110deg,
      rgba(255, 255, 255, 0.06) 20%,
      rgba(255, 255, 255, 0.24) 38%,
      rgba(255, 255, 255, 0.06) 55%
    );
    background-size: 220% 100%;
    animation: timeline-marker-shimmer 1.25s linear infinite;
  }
</style>
